// API service for fetching data from the IB Trading System API
import { API_BASE_URL } from './http.ts';
import { AccountsApiService } from './api/accounts.ts';
import { AuthApiService } from './api/auth.ts';
import { BacktestsApiService } from './api/backtests.ts';
import type { BacktestOptimizationConfig } from './api/backtests.ts';
import { DataApiService } from './api/data.ts';
import { LogsApiService, type TradeLogFilters } from './api/logs.ts';
import { ManagedAccountsApiService } from './api/managedAccounts.ts';
import { MarketApiService } from './api/market.ts';
import { NotificationsApiService, type NotificationPreferences } from './api/notifications.ts';
import {
  OrdersApiService,
  type ContractLookupResponse,
  type ManualOrderMutationResponse,
  type ModifyOrderParams,
  type OrderLookupParams,
} from './api/orders.ts';
import {
  StrategiesApiService,
  type AddNQMMPreviousPositionParams,
  type StrategyConfigHistoryResponseData,
  type StrategyConfigMutationResponseData,
  type StrategyDbConfigResponseData,
  type StrategyDbConfigUpdate,
  type StrategyName,
  type StrategySignalFilters,
  type StrategySignalLog,
  type StrategyConfigUpdate,
} from './api/strategies.ts';
import { SystemApiService } from './api/system.ts';
import { UserAdministrationApiService } from './api/users.ts';
import type { APIResponse, MessageData } from './api/types/common.ts';
import type {
  AutomatedDataTaskRunResponse,
  AutomatedDataTask,
  BacktestCancelResponse,
  BacktestProgress,
  BacktestStrategyDefinition,
  NQMMBacktestUniverse,
  CacheStatus,
  CreateAutomatedDataTaskParams,
  DataDownloadTask,
  DataDownloadTaskIdData,
  DurableJobSnapshot,
  DataInventoryData,
  DataLakeSyncResponse,
  DataSeriesDeleteResponse,
  LakeSeriesStatusResponse,
  LatestTradingDayResponse,
  MarketDataSeries,
} from './api/types/data.ts';
import type {
  BacktestExtensionResponse,
  BacktestExecutionResult,
  BacktestHistoryData,
  BacktestHistoryDetail,
  BacktestRunConfig,
  BacktestSaveResponse,
  HistoryDeleteResponse,
  OptimizationHistoryData,
  OptimizationHistoryDetail,
  OptimizationExecutionResult,
  PortfolioHistoryData,
  PortfolioHistoryDetail,
  PortfolioExecutionResult,
  PortfolioRunConfig,
} from './api/types/backtest.ts';
import type { StrategyStatus } from './api/types/strategyStatus.ts';
import type {
  LogFileContentData,
  LogFilesData,
  LogsResponse,
  SecurityEventFilters,
  SecurityEventsData,
  SecurityAlertSettings,
  SecurityAlertSettingsStatus,
  TradeLogsData,
} from './api/types/logs.ts';
import type { NotificationsResponse, UnreadCountResponse } from './api/types/notifications.ts';
import type {
  HistoricalBar,
  MarketQuote,
  MarketQuotesData,
  MarketStatusData,
} from './api/types/market.ts';
import type {
  AdminUserDetailsData,
  User,
  UserActivationData,
} from './api/types/users.ts';
import type {
  CurrentUserContext,
  InviteResponse,
  RegistrationStatusData,
  SetupAccountResponse,
} from './api/types/auth.ts';
import type {
  ManagedAccountsData,
  ManagersData,
} from './api/types/managedAccounts.ts';
import type {
  AccountPortfolio,
  OrdersResponse,
  Position,
  PositionCloseData,
  OrderCancelData,
  PositionsResponse,
  StrategyDescriptionTag,
  StrategyTagDeleteData,
  PositionCustomStrategyData,
  PositionMetadataData,
  PositionStrategyTagsData,
} from './api/types/trading.ts';
import type {
  DebugLoggingSettingsData,
  GatewayConfigurationData,
  GatewayControlData,
  LoggingSettingsData,
  SystemControlResponse,
  SystemControlStateResponse,
  SystemStatus,
  TradingRuntimeSettings,
} from './api/types/system.ts';
import type {
  AllStrategiesStatus,
  MarketAnalysis,
  NQMMPreviousPositionsData,
  TradingSignal,
} from './api/types/strategyStatus.ts';
export { ApiError } from './http.ts';
export type { APIResponse, MessageData } from './api/types/common.ts';
export type { BacktestOptimizationConfig } from './api/backtests.ts';
export type {
  CurrentUserContext,
  InviteResponse,
  RegistrationStatusData,
  SetupAccountResponse,
} from './api/types/auth.ts';
export type {
  ManagedAccountSummary,
  ManagedAccountsData,
  ManagersData,
} from './api/types/managedAccounts.ts';
export type {
  AutomatedDataTaskRunResponse,
  AutomatedDataTask,
  BacktestCancelResponse,
  BacktestProgress,
  BacktestStrategyDefinition,
  NQMMBacktestUniverse,
  AutomatedDataTaskRecord,
  AutomatedDataTasksData,
  CacheStatus,
  CreateAutomatedDataTaskParams,
  DataDownloadTask,
  DurableJobSnapshot,
  MarketDataBar,
  MarketDataInventoryItem,
  MarketDataSeries,
  DataInventoryData,
  DataLakeSyncResponse,
  DataSeriesDeleteResponse,
  LakeSeriesStatusResponse,
  LatestTradingDayResponse,
} from './api/types/data.ts';
export type {
  BacktestExtensionResponse,
  BacktestExecutionResult,
  BacktestHistoryData,
  BacktestHistoryDetail,
  BacktestHistorySummary,
  BacktestMetrics,
  BacktestRunConfig,
  BacktestSaveResponse,
  HistoryDeleteResponse,
  OptimizationHistoryData,
  OptimizationHistoryDetail,
  OptimizationExecutionResult,
  OptimizationHistorySummary,
  PortfolioHistoryData,
  PortfolioHistoryDetail,
  PortfolioHistorySummary,
  PortfolioExecutionResult,
  PortfolioRunConfig,
} from './api/types/backtest.ts';
export type {
  StrategySignalLog,
};
export type {
  NQMMPreviousPosition,
  NQMMRankingRow,
  NQMMStrategyInfo,
  ORBStrategyStatus,
  StrategyStatus,
} from './api/types/strategyStatus.ts';
export type {
  LogEntry,
  LogsResponse,
  SecurityEvent,
  SecurityEventFilters,
  SecurityEventsData,
  TradeLogEntry,
} from './api/types/logs.ts';
export type {
  LogFileContentData,
  LogFileSummary,
  LogFilesData,
  LogEntriesData,
  TradeLogsData,
} from './api/types/logs.ts';
export type {
  Notification,
  NotificationsResponse,
  UnreadCountResponse,
} from './api/types/notifications.ts';
export type { HistoricalBar, MarketStatusData } from './api/types/market.ts';
export type {
  AdminUserDetailsData,
  AuthTokenResponse,
  User,
  UserActivationData,
} from './api/types/users.ts';
export type {
  ContractLookupResponse,
  ManualOrderContract,
  ManualOrderMutationResponse,
  ModifyOrderParams,
  OrderLookupParams,
  OrderPlacementParams,
} from './api/orders.ts';
export type {
  AccountPortfolio,
  Order,
  OrdersResponse,
  OrderCancelData,
  Position,
  PositionCustomStrategyData,
  PositionCloseData,
  PositionMetadataData,
  PositionStrategyTagsData,
  PositionsResponse,
  StrategyDescriptionTag,
  StrategyTagDeleteData,
} from './api/types/trading.ts';
export type {
  GatewayStatus,
  GatewayConfigurationData,
  GatewayControlData,
  LoggingSettingsData,
  DebugLoggingSettingsData,
  SystemControl,
  SystemControlResponse,
  SystemControlStateResponse,
  SystemStatus,
  SystemStatusData,
  TradingRuntimeSettings,
} from './api/types/system.ts';
export type {
  AllStrategiesStatus,
  MarketAnalysis,
  NQMMPreviousPositionsData,
  TradingSignal,
} from './api/types/strategyStatus.ts';

class TradingApiService {
  private readonly accountsApi: AccountsApiService;
  private readonly authApi: AuthApiService;
  private readonly backtestsApi: BacktestsApiService;
  private readonly dataApi: DataApiService;
  private readonly logsApi: LogsApiService;
  private readonly managedAccountsApi: ManagedAccountsApiService;
  private readonly marketApi: MarketApiService;
  private readonly notificationsApi: NotificationsApiService;
  private readonly ordersApi: OrdersApiService;
  private readonly strategiesApi: StrategiesApiService;
  private readonly systemApi: SystemApiService;
  private readonly userAdministrationApi: UserAdministrationApiService;

  constructor(baseUrl: string = API_BASE_URL) {
    this.accountsApi = new AccountsApiService(baseUrl);
    this.authApi = new AuthApiService(baseUrl);
    this.backtestsApi = new BacktestsApiService(baseUrl);
    this.dataApi = new DataApiService(baseUrl);
    this.logsApi = new LogsApiService(baseUrl);
    this.managedAccountsApi = new ManagedAccountsApiService(baseUrl);
    this.marketApi = new MarketApiService(baseUrl);
    this.notificationsApi = new NotificationsApiService(baseUrl);
    this.ordersApi = new OrdersApiService(baseUrl);
    this.strategiesApi = new StrategiesApiService(baseUrl);
    this.systemApi = new SystemApiService(baseUrl);
    this.userAdministrationApi = new UserAdministrationApiService(baseUrl);
  }

  async login(username: string, password: string): Promise<{ access_token: string, token_type: string }> {
    return this.authApi.login(username, password);
  }

  async signup(
    email: string,
    password: string,
    role: string = 'trader',
    firstName?: string,
    lastName?: string,
    isCompany?: boolean,
    companyName?: string
  ): Promise<User> {
    return this.authApi.signup(email, password, role, firstName, lastName, isCompany, companyName);
  }

  async getRegistrationStatus(): Promise<RegistrationStatusData> {
    return this.authApi.getRegistrationStatus();
  }

  async joinWaitlist(email: string, firstName?: string, lastName?: string): Promise<{ success: boolean; message: string }> {
    return this.authApi.joinWaitlist(email, firstName, lastName);
  }

  // --- Onboarding ---
  async sendInvite(userId: number): Promise<InviteResponse> {
    return this.authApi.sendInvite(userId);
  }

  async activateUser(userId: number): Promise<APIResponse<UserActivationData>> {
    return this.authApi.activateUser(userId);
  }

  async verifyInvite(token: string): Promise<{ valid: boolean; email?: string; username?: string }> {
    return this.authApi.verifyInvite(token);
  }

  async setupAccount(token: string, password: string): Promise<SetupAccountResponse> {
    return this.authApi.setupAccount(token, password);
  }

  async getUsers(): Promise<User[]> {
    return this.userAdministrationApi.getUsers();
  }

  async adminCreateUser(user: Partial<User> & { password: string }): Promise<User> {
    return this.userAdministrationApi.adminCreateUser(user);
  }

  async adminUpdateUser(userId: number, user: Partial<User> & { password?: string }): Promise<User> {
    return this.userAdministrationApi.adminUpdateUser(userId, user);
  }

  async deleteUser(userId: number): Promise<APIResponse<MessageData>> {
    return this.userAdministrationApi.deleteUser(userId);
  }

  async getUserDetails(userId: number): Promise<APIResponse<AdminUserDetailsData>> {
    return this.userAdministrationApi.getUserDetails(userId);
  }

  async adminConfigureUserGateway(
    userId: number,
    config: {
      username: string;
      password: string;
      mode: 'paper' | 'live';
      gateway_id?: string;
      public_port?: number;
      original_gateway_id?: string;
    }
  ): Promise<APIResponse<GatewayConfigurationData>> {
    return this.userAdministrationApi.adminConfigureUserGateway(userId, config);
  }

  async adminControlUserGateway(
    userId: number,
    gatewayId: string,
    action: 'start' | 'stop' | 'restart'
  ): Promise<APIResponse<GatewayControlData>> {
    return this.userAdministrationApi.adminControlUserGateway(userId, gatewayId, action);
  }

  async adminActivateUserGateway(userId: number, gatewayId: string): Promise<APIResponse<GatewayConfigurationData>> {
    return this.userAdministrationApi.adminActivateUserGateway(userId, gatewayId);
  }

  async adminDeleteUserGateway(userId: number, gatewayId: string): Promise<APIResponse<GatewayConfigurationData>> {
    return this.userAdministrationApi.adminDeleteUserGateway(userId, gatewayId);
  }

  async getPortfolio(): Promise<AccountPortfolio> {
    return this.accountsApi.getPortfolio();
  }

  async getPositions(): Promise<PositionsResponse> {
    return this.accountsApi.getPositions();
  }

  async getHistoricalData(
    symbol: string,
    timeframe: string,
    duration: string,
    source: 'auto' | 'ibkr' = 'auto',
    signal?: AbortSignal,
  ): Promise<APIResponse & { data?: HistoricalBar[] }> {
    return this.marketApi.getHistoricalData(symbol, timeframe, duration, source, signal);
  }

  async getMarketStatus(): Promise<APIResponse<MarketStatusData>> {
    return this.marketApi.getMarketStatus();
  }

  async getCurrentUser(signal?: AbortSignal): Promise<User> {
    return this.authApi.getCurrentUser(signal);
  }

  async getStrategyDescriptionTags(): Promise<APIResponse & { data?: { tags: StrategyDescriptionTag[] } }> {
    return this.accountsApi.getStrategyDescriptionTags();
  }

  async createStrategyDescriptionTag(payload: {
    name: string;
    description?: string;
    color?: string;
  }): Promise<APIResponse & { data?: { tag: StrategyDescriptionTag } }> {
    return this.accountsApi.createStrategyDescriptionTag(payload);
  }

  async updateStrategyDescriptionTag(tagId: number, payload: {
    name?: string;
    description?: string | null;
    color?: string | null;
  }): Promise<APIResponse & { data?: { tag: StrategyDescriptionTag } }> {
    return this.accountsApi.updateStrategyDescriptionTag(tagId, payload);
  }

  async deleteStrategyDescriptionTag(tagId: number): Promise<APIResponse<StrategyTagDeleteData>> {
    return this.accountsApi.deleteStrategyDescriptionTag(tagId);
  }

  async updatePositionStrategyTags(positionKey: string, tagIds: number[]): Promise<APIResponse<PositionStrategyTagsData>> {
    return this.accountsApi.updatePositionStrategyTags(positionKey, tagIds);
  }

  async updatePositionCustomStrategy(positionKey: string, strategyName: string | null): Promise<APIResponse<PositionCustomStrategyData>> {
    return this.accountsApi.updatePositionCustomStrategy(positionKey, strategyName);
  }

  async updatePositionMetadata(
    positionKey: string,
    strategyName: string | null,
    tagIds: number[],
  ): Promise<APIResponse<PositionMetadataData>> {
    return this.accountsApi.updatePositionMetadata(positionKey, strategyName, tagIds);
  }

  async closePosition(
    symbol: string,
    secType?: string,
    contract?: Partial<Position>,
  ): Promise<APIResponse<PositionCloseData>> {
    return this.accountsApi.closePosition(symbol, secType, contract);
  }


  async controlIBGateway(
    action: 'start' | 'stop' | 'restart',
    gatewayId?: string,
  ): Promise<APIResponse<GatewayControlData>> {
    return this.systemApi.controlIBGateway(action, gatewayId);
  }

  async getOpenOrders(page: number = 1, pageSize: number = 5, secType?: string): Promise<OrdersResponse> {
    return this.ordersApi.getOpenOrders(page, pageSize, secType);
  }

  async getFilledOrdersToday(page: number = 1, pageSize: number = 5, secType?: string): Promise<OrdersResponse> {
    return this.ordersApi.getFilledOrdersToday(page, pageSize, secType);
  }

  async cancelOrder(orderId: number): Promise<APIResponse<OrderCancelData>> {
    return this.ordersApi.cancelOrder(orderId);
  }

  async modifyOrder(
    orderId: number,
    order: ModifyOrderParams,
  ): Promise<ManualOrderMutationResponse> {
    return this.ordersApi.modifyOrder(orderId, order);
  }

  async getTradeLogs(
    page: number = 1,
    pageSize: number = 50,
    filters: TradeLogFilters = {},
  ): Promise<APIResponse<TradeLogsData>> {
    return this.logsApi.getTradeLogs(page, pageSize, filters);
  }

  async getRecentLogs(maxLines: number = 100, levelFilter?: string, logFile: string = "all"): Promise<LogsResponse> {
    return this.logsApi.getRecentLogs(maxLines, levelFilter, logFile);
  }

  async getSecurityEvents(
    filters: SecurityEventFilters = {},
  ): Promise<APIResponse<SecurityEventsData>> {
    return this.logsApi.getSecurityEvents(filters);
  }

  async exportSecurityEvents(filters: SecurityEventFilters = {}): Promise<Blob> {
    return this.logsApi.exportSecurityEvents(filters);
  }

  async getSecurityAlertSettings(): Promise<APIResponse<SecurityAlertSettingsStatus>> {
    return this.logsApi.getSecurityAlertSettings();
  }

  async updateSecurityAlertSettings(settings: SecurityAlertSettings): Promise<APIResponse<SecurityAlertSettings>> {
    return this.logsApi.updateSecurityAlertSettings(settings);
  }

  // Generic strategy status method (for backward compatibility)
  async getStrategyStatus(): Promise<StrategyStatus> {
    return this.strategiesApi.getStrategyStatus();
  }

  // ON strategy specific methods
  async getONStrategyStatus(): Promise<StrategyStatus> {
    return this.strategiesApi.getONStrategyStatus();
  }

  async getONPositions(): Promise<StrategyStatus> {
    return this.strategiesApi.getONPositions();
  }

  // ORB strategy specific methods
  async getORBStrategyStatus(): Promise<StrategyStatus> {
    return this.strategiesApi.getORBStrategyStatus();
  }

  async getORBPositions(): Promise<APIResponse<Record<string, number>>> {
    return this.strategiesApi.getORBPositions();
  }

  // All strategies overview
  async getAllStrategiesStatus(): Promise<AllStrategiesStatus> {
    return this.strategiesApi.getAllStrategiesStatus();
  }

  async getSystemStatus(): Promise<SystemStatus> {
    return this.systemApi.getSystemStatus();
  }

  async getTradingSignal(symbol: string): Promise<TradingSignal> {
    return this.strategiesApi.getTradingSignal(symbol);
  }

  async getMarketAnalysis(symbol: string): Promise<APIResponse<MarketAnalysis>> {
    return this.strategiesApi.getMarketAnalysis(symbol);
  }

  // Generic strategy status with parameter
  async getStrategyStatusByName(strategyName: StrategyName): Promise<StrategyStatus> {
    return this.strategiesApi.getStrategyStatusByName(strategyName);
  }

  async getNQMMStrategyStatus(): Promise<StrategyStatus> {
    return this.strategiesApi.getNQMMStrategyStatus();
  }

  async getNQMMPositions(): Promise<StrategyStatus> {
    return this.strategiesApi.getNQMMPositions();
  }

  async getNQMMPreviousPositions(): Promise<APIResponse<NQMMPreviousPositionsData>> {
    return this.strategiesApi.getNQMMPreviousPositions();
  }

  async addNQMMPreviousPosition(
    position: AddNQMMPreviousPositionParams,
  ): Promise<APIResponse<NQMMPreviousPositionsData>> {
    return this.strategiesApi.addNQMMPreviousPosition(position);
  }

  async deleteNQMMPreviousPosition(
    symbol: string,
  ): Promise<APIResponse<NQMMPreviousPositionsData>> {
    return this.strategiesApi.deleteNQMMPreviousPosition(symbol);
  }

  async getSystemControlState(): Promise<SystemControlStateResponse> {
    return this.systemApi.getSystemControlState();
  }

  async setSystemEnabled(enabled: boolean): Promise<SystemControlResponse> {
    return this.systemApi.setSystemEnabled(enabled);
  }

  async getQuotes(symbols: string[]): Promise<APIResponse<MarketQuotesData>> {
    return this.marketApi.getQuotes(symbols);
  }

  async getContractQuote(contract: {
    symbol: string;
    sec_type?: string;
    con_id?: number;
    exchange?: string;
    currency?: string;
    last_trade_date_or_contract_month?: string;
    local_symbol?: string;
    trading_class?: string;
    multiplier?: string;
    prefer_live?: boolean;
  }): Promise<APIResponse<MarketQuote | null>> {
    return this.marketApi.getContractQuote(contract);
  }

  async setStrategyEnabled(strategyName: string, enabled: boolean): Promise<SystemControlResponse> {
    return this.systemApi.setStrategyEnabled(strategyName, enabled);
  }

  async emergencyLiquidate(): Promise<SystemControlResponse> {
    return this.systemApi.emergencyLiquidate();
  }

  async resetEmergencyMode(): Promise<SystemControlResponse> {
    return this.systemApi.resetEmergencyMode();
  }

  async connectIB(): Promise<SystemControlResponse> {
    return this.systemApi.connectIB();
  }

  async disconnectIB(): Promise<SystemControlResponse> {
    return this.systemApi.disconnectIB();
  }

  // --- Strategy Configuration (User-Specific) ---
  async getStrategyDbConfig(
    strategyName: string,
  ): Promise<APIResponse<StrategyDbConfigResponseData>> {
    return this.strategiesApi.getStrategyDbConfig(strategyName);
  }

  async getStrategyDbConfigHistory(
    strategyName: string,
  ): Promise<APIResponse<StrategyConfigHistoryResponseData>> {
    return this.strategiesApi.getStrategyDbConfigHistory(strategyName);
  }

  // --- Signals & Audit ---
  async getStrategySignals(
    params: StrategySignalFilters,
  ): Promise<APIResponse<StrategySignalLog[]>> {
    return this.strategiesApi.getStrategySignals(params);
  }

  async resetStrategyDbConfig(
    strategyName: string,
  ): Promise<APIResponse<StrategyConfigMutationResponseData>> {
    return this.strategiesApi.resetStrategyDbConfig(strategyName);
  }

  async updateStrategyConfig<TConfig extends object = Record<string, unknown>>(
    strategyName: string,
    payload: StrategyConfigUpdate<TConfig>,
  ): Promise<APIResponse<StrategyConfigMutationResponseData>> {
    return this.strategiesApi.updateStrategyConfig(strategyName, payload);
  }

  async updateStrategyDbConfig<TConfig extends object = Record<string, unknown>>(
    strategyName: string,
    payload: StrategyDbConfigUpdate<TConfig>,
  ): Promise<APIResponse<StrategyConfigMutationResponseData>> {
    return this.strategiesApi.updateStrategyDbConfig(strategyName, payload);
  }

  // --- Admin Log Management ---
  async listLogFiles(): Promise<APIResponse<LogFilesData>> {
    return this.logsApi.listLogFiles();
  }

  async getLoggingSettings(): Promise<APIResponse<LoggingSettingsData>> {
    return this.logsApi.getLoggingSettings();
  }

  async setDebugLogging(enabled: boolean): Promise<APIResponse<DebugLoggingSettingsData>> {
    return this.logsApi.setDebugLogging(enabled);
  }

  async getTradingRuntimeSettings(): Promise<APIResponse<TradingRuntimeSettings>> {
    return this.systemApi.getTradingRuntimeSettings();
  }

  async updateTradingRuntimeSettings(
    settings: Partial<TradingRuntimeSettings>,
  ): Promise<APIResponse<TradingRuntimeSettings>> {
    return this.systemApi.updateTradingRuntimeSettings(settings);
  }

  async getLogFileContent(
    filename: string,
    page: number = 1,
    pageSize: number = 100,
    level?: string,
    search?: string,
    startDate?: string,
    endDate?: string
  ): Promise<APIResponse<LogFileContentData>> {
    return this.logsApi.getLogFileContent(filename, page, pageSize, level, search, startDate, endDate);
  }

  async exportLogFile(filename: string): Promise<Blob> {
    return this.logsApi.exportLogFile(filename);
  }

  async configureIBGateway(
    username: string,
    password: string,
    mode: 'paper' | 'live' = 'paper',
    gatewayId?: string,
    publicPort?: number,
    originalGatewayId?: string,
  ): Promise<APIResponse<GatewayConfigurationData>> {
    return this.systemApi.configureIBGateway(username, password, mode, gatewayId, publicPort, originalGatewayId);
  }

  async deleteIBGatewayConfig(gatewayId: string): Promise<APIResponse<GatewayConfigurationData>> {
    return this.systemApi.deleteIBGatewayConfig(gatewayId);
  }

  async activateIBGateway(gatewayId: string): Promise<APIResponse<GatewayConfigurationData>> {
    return this.systemApi.activateIBGateway(gatewayId);
  }

  // --- Manual Orders ---
  async placeOrder(order: {
    symbol: string;
    action: 'BUY' | 'SELL';
    quantity: number;
    order_type: string;
    strategy_name?: 'MANUAL' | 'NQMM';
    price?: number;
    trailing_amount?: number;
    trailing_percent?: number;
    sec_type?: string;
    tif?: 'DAY' | 'GTC';
    outside_rth?: boolean;
    con_id?: number;
    exchange?: string;
    currency?: string;
    last_trade_date_or_contract_month?: string;
    local_symbol?: string;
    trading_class?: string;
    multiplier?: string;
  }): Promise<ManualOrderMutationResponse> {
    return this.ordersApi.placeOrder(order);
  }

  async lookupOrderContracts(params: OrderLookupParams): Promise<ContractLookupResponse> {
    return this.ordersApi.lookupOrderContracts(params);
  }

  async getBacktestStrategies(): Promise<BacktestStrategyDefinition[]> {
    return this.backtestsApi.getStrategies();
  }

  async getNQMMBacktestUniverse(): Promise<NQMMBacktestUniverse> {
    return this.backtestsApi.getNQMMUniverse();
  }

  async getLatestTradingDay(): Promise<LatestTradingDayResponse> {
    return this.dataApi.getLatestTradingDay();
  }

  async getBacktestProgress(): Promise<BacktestProgress> {
    return this.backtestsApi.getProgress();
  }

  async cancelBacktest(): Promise<BacktestCancelResponse> {
    return this.backtestsApi.cancel();
  }

  async runBacktest(
    config: BacktestRunConfig,
    onQueued?: (jobId: string) => void,
    onProgress?: (snapshot: DurableJobSnapshot) => void,
  ): Promise<BacktestExecutionResult> {
    return this.backtestsApi.run(config, onQueued, onProgress);
  }

  async checkBacktestCache(config: {
    strategy: string;
    symbol: string;
    start_date: string;
    end_date: string;
    timeframe?: string;
    capital?: number;
    data_source?: string;
    parameters?: Record<string, any>;
    execution_price_type?: string;
    entry_type?: string;
    exit_type?: string;
    sec_type?: string;
    refetch?: boolean;
  }): Promise<CacheStatus> {
    return this.dataApi.checkBacktestCache(config);
  }

  async getMarketDataInventory(
    filters: { symbol?: string; timeframe?: string; sec_type?: string; data_source?: string; storage?: string; data_type?: string } = {},
    pagination: { skip?: number; limit?: number } = {},
  ): Promise<DataInventoryData> {
    return this.dataApi.getMarketDataInventory(filters, pagination);
  }

  async deleteMarketDataSeries(params: {
    symbol: string;
    timeframe: string;
    sec_type?: string;
    data_source?: string;
    multiplier?: number;
    data_type?: string;
  }): Promise<DataSeriesDeleteResponse> {
    return this.dataApi.deleteMarketDataSeries(params);
  }

  async getLakeBackupStatus(params: {
    symbol: string;
    timeframe: string;
    sec_type?: string;
    data_source?: string;
    multiplier?: number;
    data_type?: string;
  }): Promise<LakeSeriesStatusResponse> {
    return this.dataApi.getLakeBackupStatus(params);
  }

  async syncMarketDataToLake(params: {
    symbol: string;
    timeframe: string;
    sec_type?: string;
    data_source?: string;
    multiplier?: number;
    data_type?: string;
  }): Promise<DataLakeSyncResponse> {
    return this.dataApi.syncMarketDataToLake(params);
  }

  async getMarketDataSeries(filters: {
    symbol: string;
    timeframe: string;
    sec_type?: string;
    data_source?: string;
    storage?: string;
    data_type?: string;
    multiplier?: number;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
    min_price?: number;
    max_price?: number;
    min_volume?: number;
  }): Promise<MarketDataSeries> {
    return this.dataApi.getMarketDataSeries(filters);
  }

  async getDataDownloadTasks(): Promise<DataDownloadTask[]> {
    return this.dataApi.getDataDownloadTasks();
  }

  async getDataDownloadTask(taskId: string): Promise<DataDownloadTask> {
    return this.dataApi.getDataDownloadTask(taskId);
  }

  async createDataDownloadTask(config: {
    symbol?: string;
    symbols?: string[];
    strategy?: string;
    start_date: string;
    end_date: string;
    timeframe: string;
    data_source: 'IBKR';
    sec_type?: string;
    stk_type?: string;
    exchange?: string;
    currency?: string;
    con_id?: number;
    last_trade_date_or_contract_month?: string;
    local_symbol?: string;
    trading_class?: string;
    multiplier?: string;
    storage_target?: 'both' | 'trader' | 'lake';
    data_type?: string;
    refetch?: boolean;
    fetch_profile?: string;
    chunk_days?: number;
    pacing_seconds?: number;
    max_retries?: number;
  }, idempotencyKey?: string): Promise<DataDownloadTask> {
    return this.dataApi.createDataDownloadTask(config, idempotencyKey);
  }

  async cancelDataDownloadTask(taskId: string): Promise<APIResponse<DataDownloadTaskIdData>> {
    return this.dataApi.cancelDataDownloadTask(taskId);
  }

  async getAutomatedDataTasks(): Promise<AutomatedDataTask[]> {
    return this.dataApi.getAutomatedDataTasks();
  }

  async createAutomatedDataTask(task: CreateAutomatedDataTaskParams): Promise<AutomatedDataTask> {
    return this.dataApi.createAutomatedDataTask(task);
  }

  async updateAutomatedDataTask(taskId: number, updates: Partial<CreateAutomatedDataTaskParams>): Promise<AutomatedDataTask> {
    return this.dataApi.updateAutomatedDataTask(taskId, updates);
  }

  async deleteAutomatedDataTask(taskId: number): Promise<{ success: boolean; message?: string }> {
    return this.dataApi.deleteAutomatedDataTask(taskId);
  }

  async runAutomatedDataTask(taskId: number, idempotencyKey?: string): Promise<AutomatedDataTaskRunResponse> {
    return this.dataApi.runAutomatedDataTask(taskId, idempotencyKey);
  }

  async getDurableJob(jobId: string): Promise<DurableJobSnapshot> {
    return this.dataApi.getDurableJob(jobId);
  }

  async cancelDurableJob(jobId: string): Promise<DurableJobSnapshot> {
    return this.dataApi.cancelDurableJob(jobId);
  }

  async runPortfolioBacktest(
    config: PortfolioRunConfig,
    onQueued?: (jobId: string) => void,
  ): Promise<PortfolioExecutionResult> {
    return this.backtestsApi.runPortfolio(config, onQueued);
  }

  async savePortfolioBacktest(result: Record<string, unknown>): Promise<BacktestSaveResponse> {
    return this.backtestsApi.savePortfolio(result);
  }

  async runOptimization(
    config: BacktestOptimizationConfig,
    onQueued?: (jobId: string) => void,
    onProgress?: (snapshot: DurableJobSnapshot) => void,
  ): Promise<OptimizationExecutionResult> {
    return this.backtestsApi.runOptimization(config, onQueued, onProgress);
  }

  async saveBacktest(result: Record<string, unknown>): Promise<BacktestSaveResponse> {
    return this.backtestsApi.saveBacktest(result);
  }

  async getBacktestHistory(
    page: number = 1,
    pageSize: number = 20,
    startDate?: string,
    endDate?: string,
    scope: 'all' | 'mine' | 'shared' = 'all',
  ): Promise<BacktestHistoryData> {
    return this.backtestsApi.getHistory(page, pageSize, startDate, endDate, scope);
  }

  async getBacktestDetail(id: number): Promise<BacktestHistoryDetail> {
    return this.backtestsApi.getDetail(id);
  }

  async getBacktestShareUsers() { return this.backtestsApi.getShareUsers(); }
  async getBacktestSharing(id: number) { return this.backtestsApi.getSharing(id); }
  async updateBacktestSharing(id: number, shareAll: boolean, userIds: number[]) {
    return this.backtestsApi.updateSharing(id, shareAll, userIds);
  }

  async extendBacktest(id: number, config: { end_date?: string; sec_type?: string; refetch?: boolean; re_run_oos?: boolean; engine?: string; isOptimization?: boolean } = {}): Promise<BacktestExtensionResponse> {
    return this.backtestsApi.extend(id, config);
  }

  async deleteBacktest(id: number): Promise<HistoryDeleteResponse> {
    return this.backtestsApi.delete(id);
  }

  async saveOptimization(result: Record<string, unknown>): Promise<BacktestSaveResponse> {
    return this.backtestsApi.saveOptimization(result);
  }

  async getOptimizationHistory(skip = 0, limit = 20): Promise<OptimizationHistoryData> {
    return this.backtestsApi.getOptimizationHistory(skip, limit);
  }

  async getOptimizationDetail(id: number): Promise<OptimizationHistoryDetail> {
    return this.backtestsApi.getOptimizationDetail(id);
  }

  async deleteOptimization(id: number): Promise<HistoryDeleteResponse> {
    return this.backtestsApi.deleteOptimization(id);
  }

  // --- Managed Accounts ---
  async getManagedAccounts(): Promise<APIResponse<ManagedAccountsData>> {
    return this.managedAccountsApi.getManagedAccounts();
  }

  async addManagedAccount(managedUserId: number): Promise<APIResponse<MessageData>> {
    return this.managedAccountsApi.addManagedAccount(managedUserId);
  }

  async removeManagedAccount(managedUserId: number): Promise<APIResponse<MessageData>> {
    return this.managedAccountsApi.removeManagedAccount(managedUserId);
  }

  async switchToManagedAccount(managedUserId: number): Promise<{ access_token: string; token_type: string }> {
    return this.managedAccountsApi.switchToManagedAccount(managedUserId);
  }

  async switchBackToOwnAccount(): Promise<{ access_token: string; token_type: string }> {
    return this.managedAccountsApi.switchBackToOwnAccount();
  }

  async getCurrentContext(): Promise<APIResponse<CurrentUserContext>> {
    return this.authApi.getCurrentContext();
  }

  // --- Admin: Managed Accounts for Any User ---
  async adminGetUserManagers(userId: number): Promise<APIResponse<ManagersData>> {
    return this.userAdministrationApi.adminGetUserManagers(userId);
  }

  async adminAddUserManager(userId: number, managerUserId: number): Promise<APIResponse<MessageData>> {
    return this.userAdministrationApi.adminAddUserManager(userId, managerUserId);
  }

  async adminRemoveUserManager(userId: number, managerUserId: number): Promise<APIResponse<MessageData>> {
    return this.userAdministrationApi.adminRemoveUserManager(userId, managerUserId);
  }

  // --- Notifications ---
  async getNotifications(limit: number = 50, unreadOnly: boolean = false): Promise<NotificationsResponse> {
    return this.notificationsApi.getNotifications(limit, unreadOnly);
  }

  async getUnreadCount(): Promise<UnreadCountResponse> {
    return this.notificationsApi.getUnreadCount();
  }

  async markNotificationRead(id: number): Promise<APIResponse<null>> {
    return this.notificationsApi.markNotificationRead(id);
  }

  async markAllRead(): Promise<APIResponse<null>> {
    return this.notificationsApi.markAllRead();
  }

  async updateNotificationPreferences(prefs: NotificationPreferences): Promise<APIResponse<MessageData>> {
    return this.notificationsApi.updateNotificationPreferences(prefs);
  }

  // --- Portfolio History ---
  async getPortfolioHistory(skip: number = 0, limit: number = 100): Promise<PortfolioHistoryData> {
    return this.backtestsApi.getPortfolioHistory(skip, limit);
  }

  async deletePortfolio(id: number): Promise<HistoryDeleteResponse> {
    return this.backtestsApi.deletePortfolio(id);
  }

  async getPortfolioById(id: number): Promise<PortfolioHistoryDetail> {
    return this.backtestsApi.getPortfolioById(id);
  }
}

export const tradingApi = new TradingApiService();
export default TradingApiService;
